import type { DescriptionsProps } from 'antd'
import { Descriptions, Space } from 'antd'
import AccreditationPassportDownload from 'components/Accreditations/AccreditationPassportDownload'
import PassportCustom from 'components/Accreditations/PassportCustom'
import Spinner from 'components/Spinner'
import Modal from 'components/UI/Modal'
import { API_URL_PROFILE_SWITCH, API_URL_USERS } from 'constants/api'
import { useAuth } from 'context/AuthProvider'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useQuery } from 'context/QueryProvider'
import { useToast } from 'context/ToastProvider'
import dayjs from 'dayjs'
import useSWR from 'hooks/swr'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Profile, ProfileAssignment } from 'shared/types'
import Button from './CoreUIButton'
import Link from './Link'
import SectionTitle from './SectionTitle'
import Tooltip from './Tooltip'

interface UserProfileProps {
  id: number
  onClose: () => void
}

export default function UserProfile({ id, onClose }: UserProfileProps) {
  const { client } = useQuery()
  const { user: loggedUser, refreshToken } = useAuth()
  const { i18n, i18name, locale } = useI18n()
  const navigate = useNavigate()
  const toast = useToast()
  const { data: user, error } = useSWR(`${API_URL_USERS}/${id}`)
  const { profile, profiles, hasPermission } = useProfile()
  const [showCustomPassport, setShowCustomPassport] = useState(false)
  const hasCollaboratorProfile = profiles.find((p: Profile) => p.type === 'collaborator')

  function formatGender(gender: string | number) {
    if (gender === '0' || gender === 0 || gender === '') return i18n('gender.women')
    if (gender === '1' || gender === 1) return i18n('gender.men')

    return i18n('gender.other')
  }

  const formatEmail = (email: string | null) => (email ? <Link to={`mailto:${email}`}>{email}</Link> : '-')

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    const parsed = dayjs(date)
    if (!parsed.isValid()) {
      return null
    }
    return parsed.format('DD/MM/YYYY')
  }

  const formatDateHour = (date: string | null) => (date ? dayjs(date).format('DD/MM/YYYY hh:mm:ss') : '-')

  if (!user || !loggedUser) return null

  const currentUser = loggedUser.id === id

  const contact: DescriptionsProps['items'] = [
    {
      key: 'phone',
      label: i18n('label.phone'),
      children: user.phone,
    },
    {
      key: 'email',
      label: i18n('label.email'),
      children: formatEmail(user.email),
    },
  ]

  const informations: DescriptionsProps['items'] = [
    {
      key: 'gender',
      label: i18n('label.gender'),
      children: formatGender(user.gender),
    },
    {
      key: 'userName',
      label: i18n('label.username'),
      children: user.username,
    },
    {
      key: 'birthdate',
      label: i18n('label.birthdate'),
      children: formatDate(user.birthdate),
    },
    {
      key: 'language',
      label: i18n('label.user.language'),
      children: i18n(`locale.${user.language}`),
    },
    {
      key: 'degree',
      label: i18n('label.degree'),
      children: user.degree || '-',
    },
  ]

  const organization: DescriptionsProps['items'] = [
    {
      key: 'manager',
      label: i18n('label.manager'),
      children: user.manager?.fullName || '-',
    },
    {
      key: 'managerEmail',
      label: i18n('label.manager.email'),
      children: formatEmail(user.manager?.email),
    },
    {
      key: 'entryDate',
      label: i18n('label.entry_date'),
      children: formatDate(user.employmentContract?.entryCompany),
    },
    {
      key: 'exitDate',
      label: i18n('label.exit_date'),
      children: formatDate(user.employmentContract?.exitCompany),
    },
    {
      key: 'organization',
      label: i18n('label.organization'),
      children: user.organization?.name || '-',
    },
    {
      key: 'establishment',
      label: i18n('label.establishment'),
      children: user.establishment?.name || '-',
    },
    {
      key: 'site',
      label: i18n('label.site'),
      children: user.site?.name || '-',
    },
    {
      key: 'initials',
      label: i18n('label.initials'),
      children: user.initials,
    },
  ]
  if (['advanced', 'medium'].includes(profile.userDetails) || currentUser) {
    organization.push({
      key: 'contractType',
      label: i18n('label.contract_type'),
      children: user.employmentContract?.contractType?.name || '-',
    })
  }
  if (profile.userDetails === 'advanced' || currentUser) {
    organization.push({
      key: 'csp',
      label: i18n('label.csp'),
      children: user.employmentContract?.csp?.name || '-',
    })
  }
  organization.push({
    key: 'active',
    label: i18n('label.active'),
    children: user.enabled ? i18n('yes') : i18n('no'),
  })

  if (profile.userDetails === 'advanced' || currentUser) {
    organization.push({
      key: 'rate',
      label: i18n('label.hourly_rate'),
      children: user.employmentContract?.hourlyRate || '-',
    })
  }
  organization.push({
    key: 'trainer',
    label: i18n('label.trainer'),
    children: user.trainer ? i18n('yes') : i18n('no'),
  })

  const additionalData = (user?.additionalDatas || []).map((data: ReactNode, i: number) => ({
    key: i,
    label: i18n(`userAdditionalData${i + 1}`),
    children: data,
  }))

  const complementary: DescriptionsProps['items'] = [
    {
      key: 'creationDate',
      label: i18n('label.user.creationDate'),
      children: formatDateHour(user.creationDate),
    },
    {
      key: 'updateDate',
      label: i18n('label.user.updateDate'),
      children: formatDateHour(user.updateDate),
    },
    {
      key: 'lastActivity',
      label: i18n('label.user.lastActivity'),
      children: formatDateHour(user.lastActivity),
    },
  ]

  const props = { labelStyle: { width: '12rem', marginLeft: '1.5rem' } }

  return (
    <Modal onCancel={onClose} width="900px" footer={null}>
      {showCustomPassport ? (
        <PassportCustom userId={id} onBack={() => setShowCustomPassport(false)} />
      ) : !profile || !user || error ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-8">
          <SectionTitle className="text-primary-600">{user?.fullName}</SectionTitle>

          <Descriptions
            title={
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                {i18n('profile.section.contact')}
                {profile.type !== 'collaborator' && (
                  <Space>
                    {hasPermission('ACCREDITATION_CAN_DOWNLOAD_PASSPORT') ? (
                      <AccreditationPassportDownload
                        userId={id}
                        disabled={user.hasValidAccreditations === false}
                        onCustom={() => setShowCustomPassport(true)}
                      />
                    ) : null}
                    {(profile.canViewDashboard || id === loggedUser.id) && (
                      <Tooltip
                        title={
                          id === loggedUser.id
                            ? i18n(
                                hasCollaboratorProfile
                                  ? 'profile.modal.dashboard.tooltip.bad_profile'
                                  : 'profile.modal.dashboard.tooltip.missing_collaborator_profile',
                              )
                            : null
                        }
                      >
                        <Button
                          onClick={() => {
                            if (id === loggedUser.id && hasCollaboratorProfile) {
                              client
                                .post(API_URL_PROFILE_SWITCH.replace(':id', hasCollaboratorProfile.id))
                                .then(({ token }: { token: string }) => {
                                  refreshToken(token)
                                  window.location.href = `/${locale}/dashboard/${id}`
                                })
                                .catch(toast.error)
                            } else {
                              navigate(`/${locale}/dashboard/${id}`)
                            }
                          }}
                          disabled={id === loggedUser.id && !hasCollaboratorProfile}
                        >
                          {i18n('profile.modal.button.dashboard')}
                        </Button>
                      </Tooltip>
                    )}
                  </Space>
                )}
              </div>
            }
            items={contact}
            column={2}
            {...props}
          />
          {(profile.userDetails === 'advanced' || currentUser) && (
            <Descriptions
              title={
                <div className="flex items-center justify-between border-b border-slate-200 py-2">
                  {i18n('profile.section.user_informations')}
                </div>
              }
              items={informations}
              column={2}
              {...props}
            />
          )}
          <Descriptions
            title={
              <div className="flex items-center justify-between border-b border-slate-200 py-2">
                {i18n('profile.section.organization_informations')}
              </div>
            }
            items={organization}
            column={2}
            {...props}
          />
          {(profile.userDetails === 'advanced' || currentUser) && (
            <Descriptions
              title={
                <div className="flex items-center justify-between border-b border-slate-200 py-2">
                  {i18n('profile.section.additional_datas')}
                </div>
              }
              items={additionalData}
              column={2}
              {...props}
            />
          )}
          <Descriptions
            title={
              <div className="flex items-center justify-between border-b border-slate-200 py-2">
                {i18n('profile.section.complementary_informations')}
              </div>
            }
            items={complementary}
            column={2}
            {...props}
          />
          {(profile.userDetails === 'advanced' || currentUser) && (
            <div className="w-full flex flex-col gap-4">
              <div className="flex justify-between border-b border-slate-200 py-2">
                <span className="font-bold text-lg mt-2">{i18n('profile.section.profile_informations')}</span>
                {hasPermission('ADMIN_MANAGE_PROFILE') && (
                  <Button onClick={() => window.open(`/${locale}/admin/profiles`, '_blank')}>
                    {i18n('profile.section.add_profile_button')}
                  </Button>
                )}
              </div>
              <ul className=" ml-3 px-6">
                {user.profileAssignments?.map((profileAssignment: ProfileAssignment) => (
                  <li key={profileAssignment.id} className="list-disc list-image-none">
                    {hasPermission('ADMIN_MANAGE_PROFILE') ? (
                      <Link
                        className="text-(--primary)"
                        to={
                          `/${locale}/admin/profiles/${profileAssignment?.profile?.id}?` +
                          `tab=assign&q=${user.reference}`
                        }
                        target="_blank"
                      >
                        {i18name(profileAssignment?.profile)}
                      </Link>
                    ) : (
                      i18name(profileAssignment?.profile)
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
